import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  AndroidFilled,
  CheckCircleFilled,
  CloudSyncOutlined,
  DownloadOutlined,
  FileProtectOutlined,
  LoginOutlined,
  SafetyCertificateFilled,
  TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const { Title, Paragraph, Text } = Typography;

interface PublicVersion {
  version: string;
  changelog: string;
  sha256_checksum: string;
  released_at: string;
  download_url: string;
}

type PageState = "loading" | "data" | "error";

interface VariantProps {
  state: PageState;
  data: PublicVersion | null;
  error: string | null;
  onCopy: () => void;
  formatRel: (iso: string) => string;
}

const PAGE_STYLES = `
  .rl-page * { box-sizing: border-box; }
  .rl-page { font-family: 'Inter', system-ui, -apple-system, sans-serif; color: #0f172a; }

  .rl-mono {
    display: inline-flex; align-items: center; justify-content: center;
    background: #2563eb; color: #fff; font-weight: 700;
    border-radius: 6px; letter-spacing: -0.02em;
  }

  @keyframes rlFadeUp { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
  .rl-fadeUp { animation: rlFadeUp .32s cubic-bezier(.2,.8,.2,1) both; }

  .rl-step-num {
    width: 32px; height: 32px; border-radius: 999px;
    background: #eff4ff; color: #2563eb; font-weight: 600;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 14px; flex: 0 0 32px;
  }

  .rl-trust-ico {
    width: 36px; height: 36px; border-radius: 8px;
    background: #ecfdf5; color: #10b981;
    display: inline-flex; align-items: center; justify-content: center;
    flex: 0 0 36px;
  }

  .rl-phone-frame {
    width: 280px; aspect-ratio: 9/19; border-radius: 28px;
    background: #0f172a; padding: 8px;
    box-shadow: 0 30px 60px -20px rgba(15,23,42,.35), 0 12px 24px -16px rgba(15,23,42,.3);
    position: relative;
  }
  .rl-phone-screen {
    width: 100%; height: 100%; border-radius: 22px; overflow: hidden;
    background: #f8fafc; position: relative;
    display: flex; flex-direction: column;
  }
  .rl-phone-notch {
    position: absolute; top: 14px; left: 50%; transform: translateX(-50%);
    width: 80px; height: 18px; background: #0f172a; border-radius: 999px; z-index: 2;
  }

  .rl-hero-tint {
    background:
      radial-gradient(800px 400px at 12% -10%, rgba(37,99,235,.07), transparent 60%),
      radial-gradient(600px 300px at 95% 0%, rgba(16,185,129,.05), transparent 60%);
  }

  .rl-state-pill {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 10px; border-radius: 999px;
    background: #ecfdf5; color: #047857; font-size: 12px; font-weight: 500;
  }
  .rl-state-pill .rl-dot { width: 6px; height: 6px; border-radius: 999px; background: #10b981; }

  .rl-foot {
    text-align: center; color: #94a3b8; font-size: 13px; padding: 32px 16px 40px;
  }

  .rl-mock-row {
    background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;
    padding: 10px 12px; margin: 6px 10px; font-size: 11px; color: #0f172a;
    display: flex; justify-content: space-between; align-items: center;
  }
  .rl-mock-pill { font-size: 9px; padding: 2px 6px; border-radius: 999px; font-weight: 600; }

  .rl-header {
    background: #fff; border-bottom: 1px solid #e2e8f0;
    position: sticky; top: 0; z-index: 50;
  }

  @media (max-width: 900px) {
    .rl-hero-grid-d { grid-template-columns: 1fr !important; }
    .rl-phone-wrap { padding-bottom: 32px; }
  }
`;

function Logo({ size = "sm" }: { size?: "sm" | "lg" }) {
  const px = size === "lg" ? 36 : 28;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
      <span className="rl-mono" style={{ width: px, height: px, fontSize: px * 0.45 }}>
        RL
      </span>
      <span
        style={{
          fontWeight: 600,
          color: "#0f172a",
          fontSize: size === "lg" ? 18 : 15,
          letterSpacing: "-0.01em",
        }}
      >
        Reflexlabs ERP
      </span>
    </span>
  );
}

function DesktopHeader() {
  return (
    <header className="rl-header">
      <div
        style={{
          maxWidth: 1120,
          margin: "0 auto",
          padding: "14px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Link to="/" style={{ textDecoration: "none" }}>
          <Logo />
        </Link>
        <Link to="/login">
          <Button icon={<LoginOutlined />}>Log in</Button>
        </Link>
      </div>
    </header>
  );
}

function MobileHeader() {
  return (
    <header
      style={{
        background: "#fff",
        borderBottom: "1px solid #e2e8f0",
        position: "sticky",
        top: 0,
        zIndex: 50,
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Link to="/" style={{ textDecoration: "none" }}>
        <Logo />
      </Link>
      <Link to="/login">
        <Button size="small" icon={<LoginOutlined />} aria-label="Log in">
          Log in
        </Button>
      </Link>
    </header>
  );
}

function StepRow({ n, title, body }: { n: string; title: string; body: ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span className="rl-step-num">{n}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: "#0f172a", marginBottom: 2, fontSize: 15 }}>
          {title}
        </div>
        <div style={{ color: "#475569", fontSize: 14, lineHeight: 1.5 }}>{body}</div>
      </div>
    </div>
  );
}

function InstallSteps() {
  return (
    <Card bordered bodyStyle={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
        <AndroidFilled style={{ color: "#10b981", fontSize: 18 }} />
        <Text strong style={{ fontSize: 15 }}>
          Install on your Android phone
        </Text>
      </div>
      <Space direction="vertical" size={18} style={{ width: "100%" }}>
        <StepRow
          n="1"
          title="Download the APK"
          body="Tap the Download APK button above. The file lands in your Downloads folder."
        />
        <StepRow
          n="2"
          title="Allow installs from this source"
          body={
            <>
              If Android prompts, tap <Text code style={{ fontSize: 13 }}>Settings</Text> and toggle{" "}
              <b>Allow from this source</b>. You only need to do this once.
            </>
          }
        />
        <StepRow
          n="3"
          title="Open the file and tap Install"
          body="Open the APK from your notifications or Downloads and tap Install. Sign in with your Reflexlabs credentials."
        />
      </Space>
    </Card>
  );
}

function TrustStrip() {
  const items = [
    {
      icon: <CloudSyncOutlined />,
      title: "Auto-updates inside the app",
      body: "New versions install themselves; no Play Store needed.",
    },
    {
      icon: <SafetyCertificateFilled />,
      title: "SHA-256 verified downloads",
      body: "Every build is checksum-verified before install.",
    },
    {
      icon: <TeamOutlined />,
      title: "Used by Reflexlabs internal teams",
      body: "Battle-tested across all field operations daily.",
    },
  ];
  return (
    <div
      style={{
        display: "grid",
        gap: 16,
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
      }}
    >
      {items.map((it, i) => (
        <div
          key={i}
          style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 6,
            padding: 18,
            display: "flex",
            gap: 14,
            alignItems: "flex-start",
          }}
        >
          <span className="rl-trust-ico">{it.icon}</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{it.title}</div>
            <div style={{ color: "#64748b", fontSize: 13, lineHeight: 1.5 }}>{it.body}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PageFooter() {
  return (
    <footer className="rl-foot">
      © Reflexlabs · made by Reflexlabs · Need help? Contact your manager.
    </footer>
  );
}

function VersionCard({
  data,
  onCopy,
  formatRel,
  compact = false,
}: {
  data: PublicVersion;
  onCopy: () => void;
  formatRel: (iso: string) => string;
  compact?: boolean;
}) {
  return (
    <div className="rl-fadeUp">
      <Card bordered bodyStyle={{ padding: compact ? 18 : 24 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <Tag
            color="blue"
            style={{
              fontSize: 13,
              padding: "2px 10px",
              borderRadius: 999,
              fontWeight: 600,
              margin: 0,
            }}
          >
            v{data.version}
          </Tag>
          <span style={{ color: "#64748b", fontSize: 13 }}>
            released {formatRel(data.released_at)}
          </span>
          <span className="rl-state-pill" style={{ marginLeft: "auto" }}>
            <span className="rl-dot" />
            Latest stable
          </span>
        </div>

        <Text strong style={{ fontSize: 14, color: "#0f172a" }}>
          What's new
        </Text>
        <Paragraph
          style={{
            whiteSpace: "pre-wrap",
            marginTop: 8,
            marginBottom: 16,
            color: "#334155",
            fontSize: 14,
            lineHeight: 1.6,
          }}
        >
          {data.changelog || "No changelog provided."}
        </Paragraph>

        <div
          style={{
            paddingTop: 14,
            borderTop: "1px dashed #e2e8f0",
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <FileProtectOutlined style={{ color: "#64748b" }} />
          <Text type="secondary" style={{ fontSize: 13 }}>
            SHA-256
          </Text>
          <Text code copyable={{ text: data.sha256_checksum, onCopy }} style={{ fontSize: 12 }}>
            {data.sha256_checksum.slice(0, 16)}…
          </Text>
        </div>
      </Card>
    </div>
  );
}

function VersionSlot({ state, data, error, onCopy, formatRel, compact }: VariantProps & { compact?: boolean }) {
  if (state === "error" || !data) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Release unavailable"
        description={error || "No release is available yet. Please check back later."}
      />
    );
  }
  return <VersionCard data={data} onCopy={onCopy} formatRel={formatRel} compact={compact} />;
}

function DownloadCTA({
  state,
  data,
  large = true,
  block = false,
}: {
  state: PageState;
  data: PublicVersion | null;
  large?: boolean;
  block?: boolean;
}) {
  if (state === "error" || !data) return null;
  return (
    <Button
      type="primary"
      size={large ? "large" : "middle"}
      icon={<DownloadOutlined />}
      href={data.download_url}
      block={block}
      aria-label="Download Reflexlabs ERP APK"
      style={{
        height: large ? 52 : undefined,
        paddingInline: large ? 28 : undefined,
        fontSize: large ? 16 : undefined,
        fontWeight: 600,
        boxShadow: "0 8px 18px -8px rgba(37,99,235,.55)",
      }}
    >
      Download APK · v{data.version}
    </Button>
  );
}

function PhoneMock({ scale = 1 }: { scale?: number }) {
  const rows = [
    { id: "ORD-2418", title: "Hydraulic seal kit", meta: "Site B · K. Patel", state: "Pending", color: "#f59e0b", bg: "#fef3c7" },
    { id: "ORD-2417", title: "Replacement battery", meta: "Site A · J. Khan", state: "Approved", color: "#047857", bg: "#d1fae5" },
    { id: "ORD-2416", title: "Cable harness, 2m", meta: "Site C · M. Diaz", state: "Pending", color: "#f59e0b", bg: "#fef3c7" },
    { id: "ORD-2415", title: "Dust filter (x4)", meta: "Site B · A. Roy", state: "Approved", color: "#047857", bg: "#d1fae5" },
    { id: "ORD-2414", title: "Safety harness", meta: "Site A · L. Kim", state: "Delivered", color: "#0369a1", bg: "#e0f2fe" },
  ];
  return (
    <div className="rl-phone-frame" style={{ transform: `scale(${scale})`, transformOrigin: "top center" }}>
      <div className="rl-phone-notch" />
      <div className="rl-phone-screen">
        <div
          style={{
            padding: "6px 16px 4px",
            display: "flex",
            justifyContent: "space-between",
            fontSize: 10,
            color: "#475569",
            fontWeight: 600,
          }}
        >
          <span>9:41</span>
          <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
            <span
              style={{
                display: "inline-block",
                width: 14,
                height: 8,
                border: "1px solid currentColor",
                borderRadius: 2,
              }}
            >
              <span style={{ display: "block", width: 10, height: 4, margin: "1px", background: "currentColor" }} />
            </span>
          </span>
        </div>
        <div
          style={{
            padding: "20px 16px 12px",
            background: "#fff",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span className="rl-mono" style={{ width: 22, height: 22, fontSize: 11 }}>
              RL
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#0f172a" }}>My Orders</span>
            <span style={{ marginLeft: "auto", fontSize: 9, color: "#64748b" }}>v3.4.2</span>
          </div>
          <div
            style={{
              background: "#f1f5f9",
              borderRadius: 6,
              padding: "6px 10px",
              fontSize: 10,
              color: "#64748b",
            }}
          >
            Search orders, parts, vendors…
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 16,
            padding: "8px 16px",
            fontSize: 10,
            color: "#64748b",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <span style={{ color: "#2563eb", fontWeight: 600, borderBottom: "2px solid #2563eb", paddingBottom: 4 }}>
            Pending (4)
          </span>
          <span>Approved</span>
          <span>Delivered</span>
        </div>
        <div style={{ padding: "4px 0", flex: 1, overflow: "hidden" }}>
          {rows.map((r) => (
            <div key={r.id} className="rl-mock-row">
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 2 }}>{r.title}</div>
                <div style={{ color: "#64748b", fontSize: 9 }}>
                  {r.id} · {r.meta}
                </div>
              </div>
              <span className="rl-mock-pill" style={{ background: r.bg, color: r.color }}>
                {r.state}
              </span>
            </div>
          ))}
        </div>
        <div
          style={{
            position: "absolute",
            right: 16,
            bottom: 18,
            width: 44,
            height: 44,
            borderRadius: 999,
            background: "#2563eb",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 20,
            fontWeight: 300,
            boxShadow: "0 8px 16px -4px rgba(37,99,235,.45)",
          }}
        >
          +
        </div>
      </div>
    </div>
  );
}

function VariantD(props: VariantProps) {
  const { state, data } = props;
  return (
    <>
      <DesktopHeader />
      <main className="rl-hero-tint">
        <div
          className="rl-hero-grid-d"
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            padding: "56px 24px 0",
            display: "grid",
            gap: 32,
            alignItems: "center",
            gridTemplateColumns: "minmax(0, 1fr) auto",
          }}
        >
          <div style={{ paddingBottom: 56 }}>
            <Tag
              bordered={false}
              color="blue"
              style={{ borderRadius: 999, padding: "4px 12px", marginBottom: 22, fontWeight: 500 }}
            >
              <AndroidFilled /> &nbsp; Android only
            </Tag>
            <Title
              level={1}
              style={{
                fontSize: "clamp(34px, 4.6vw, 56px)",
                lineHeight: 1.05,
                margin: "0 0 18px",
                letterSpacing: "-0.025em",
                fontWeight: 700,
              }}
            >
              The order app for
              <br />
              your field team.
            </Title>
            <Paragraph
              style={{
                fontSize: 18,
                color: "#475569",
                margin: "0 0 28px",
                lineHeight: 1.5,
                maxWidth: 460,
              }}
            >
              Place, approve, and track employee orders from your Android phone.
            </Paragraph>

            <Space size={14} wrap align="center">
              <DownloadCTA state={state} data={data} />
              <span
                style={{
                  display: "inline-flex",
                  flexDirection: "column",
                  fontSize: 12,
                  color: "#64748b",
                }}
              >
                <span style={{ color: "#0f172a", fontWeight: 600, fontSize: 13 }}>
                  {state === "data" && data ? `v${data.version}` : "—"}
                </span>
                <span>Android 9.0+</span>
              </span>
            </Space>
            <div
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "#64748b",
                fontSize: 13,
              }}
            >
              <CheckCircleFilled style={{ color: "#10b981" }} />
              Android only · Free for licensed employees
            </div>
          </div>

          <div className="rl-phone-wrap" style={{ display: "flex", justifyContent: "center", paddingTop: 8 }}>
            <PhoneMock />
          </div>
        </div>
      </main>

      <section style={{ maxWidth: 1120, margin: "0 auto", padding: "40px 24px 0" }}>
        <VersionSlot {...props} />
      </section>

      <section style={{ maxWidth: 1120, margin: "0 auto", padding: "32px 24px 0" }}>
        <InstallSteps />
      </section>

      <section style={{ maxWidth: 1120, margin: "0 auto", padding: "32px 24px 48px" }}>
        <TrustStrip />
      </section>

      <PageFooter />
    </>
  );
}

function VariantDMobile(props: VariantProps) {
  const { state, data } = props;
  return (
    <div style={{ background: "#f5f5f5", minHeight: "100%" }}>
      <MobileHeader />

      <main className="rl-hero-tint" style={{ padding: "28px 20px 8px" }}>
        <Tag
          bordered={false}
          color="blue"
          style={{ borderRadius: 999, padding: "3px 10px", marginBottom: 14, fontWeight: 500, fontSize: 12 }}
        >
          <AndroidFilled /> &nbsp; Android only
        </Tag>
        <Title
          level={1}
          style={{
            fontSize: 32,
            lineHeight: 1.1,
            margin: "0 0 12px",
            letterSpacing: "-0.025em",
            fontWeight: 700,
          }}
        >
          The order app for your field team.
        </Title>
        <Paragraph style={{ fontSize: 16, color: "#475569", margin: "0 0 20px", lineHeight: 1.5 }}>
          Place, approve, and track employee orders from your Android phone.
        </Paragraph>

        {state !== "error" && data && (
          <Button
            type="primary"
            size="large"
            icon={<DownloadOutlined />}
            href={data.download_url}
            block
            aria-label="Download Reflexlabs ERP APK"
            style={{
              height: 52,
              fontSize: 16,
              fontWeight: 600,
              boxShadow: "0 8px 18px -8px rgba(37,99,235,.55)",
            }}
          >
            Download APK · v{data.version}
          </Button>
        )}

        <div
          style={{
            marginTop: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: "#64748b",
            fontSize: 13,
            justifyContent: "center",
          }}
        >
          <CheckCircleFilled style={{ color: "#10b981" }} />
          Android only · Free for licensed employees
        </div>

        <div style={{ display: "flex", justifyContent: "center", padding: "24px 0 8px" }}>
          <PhoneMock scale={0.78} />
        </div>
      </main>

      <section style={{ padding: "16px 16px 0" }}>
        <VersionSlot {...props} compact />
      </section>

      <section style={{ padding: "16px 16px 0" }}>
        <InstallSteps />
      </section>

      <section style={{ padding: "16px 16px 24px" }}>
        <TrustStrip />
      </section>

      <PageFooter />
    </div>
  );
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth < breakpoint
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
}

export function PublicDownload() {
  const [data, setData] = useState<PublicVersion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    const baseURL = import.meta.env.VITE_API_BASE_URL || "/api/v1";
    fetch(`${baseURL}/app/public/version-check/`)
      .then(async (res) => {
        if (res.status === 404) {
          setError("No release is available yet. Please check back later.");
          return null;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<PublicVersion>;
      })
      .then((j) => {
        if (j) setData(j);
      })
      .catch(() => setError("Could not load release info. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  const state: PageState = loading ? "loading" : error ? "error" : "data";
  const formatRel = (iso: string) => (iso ? dayjs(iso).fromNow() : "");
  const onCopy = () => {
    void message.success("SHA-256 copied");
  };

  const variantProps: VariantProps = { state, data, error, onCopy, formatRel };

  return (
    <div className="rl-page" style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <style>{PAGE_STYLES}</style>
      {loading ? (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="large" />
        </div>
      ) : isMobile ? (
        <VariantDMobile {...variantProps} />
      ) : (
        <VariantD {...variantProps} />
      )}
    </div>
  );
}
